'use client';

/**
 * CreatorMultiSelect — SDK-backed async creator picker for the discovery-feed
 * filter sheets. Wraps the {@link SearchableMultiSelect} primitive in its
 * controlled-query mode and drives a reactive `useGetSearch` (the `creators`
 * lane — the same lane the global search modal uses) so results flow entirely
 * through the Morfi SDK (no hand-rolled fetch, FQ / sdk-only rules).
 *
 * The selected `value` carries each creator's wallet address as `value` — that
 * is exactly what the `creator` query param on `/v1/markets` and `/v1/proposals`
 * expects — so the feed can join the selection into a CSV at the edge.
 */
import { c as _c } from "react/compiler-runtime";
import { useGetSearch } from '@morfi/backend-sdk-v2/client';
import { useEffect, useState } from 'react';
import { type MultiSelectOption, SearchableMultiSelect } from '@/components/ui/searchable-multi-select';
const DEBOUNCE_MS = 300;
const MIN_QUERY_LENGTH = 2;

/**
 * The display label for a creator option. Creators carry a displayName or
 * handle; prefer displayName, fall back to `@handle`. Wallet-only creators
 * (no human identity) are dropped from the option list rather than shown as a
 * raw address (the FQ-104 wallet-as-identity rule), so this returns `null` to
 * filter them out.
 */
function creatorLabel(creator: {
  displayName: string | null;
  handle: string | null;
}): string | null {
  if (creator.displayName !== null && creator.displayName.length > 0) return creator.displayName;
  if (creator.handle !== null && creator.handle.length > 0) return `@${creator.handle}`;
  return null;
}
export interface CreatorMultiSelectProps {
  value: MultiSelectOption[];
  onChange: (next: MultiSelectOption[]) => void;
  placeholder: string;
  emptyLabel: string;
}
export function CreatorMultiSelect(t0) {
  const $ = _c(22);
  const {
    value,
    onChange,
    placeholder,
    emptyLabel
  } = t0;
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  let t1;
  let t2;
  if ($[0] !== query) {
    t1 = () => {
      const id = setTimeout(() => setDebouncedQuery(query), DEBOUNCE_MS);
      return () => clearTimeout(id);
    };
    t2 = [query];
    $[0] = query;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  if ($[3] !== debouncedQuery) {
    t3 = debouncedQuery.trim();
    $[3] = debouncedQuery;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const shouldSearch = t3.length >= MIN_QUERY_LENGTH;
  let t4;
  if ($[5] !== debouncedQuery) {
    t4 = debouncedQuery.trim();
    $[5] = debouncedQuery;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t4) {
    t5 = {
      q: t4,
      type: "creators"
    };
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== shouldSearch) {
    t6 = {
      enabled: shouldSearch
    };
    $[9] = shouldSearch;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const {
    data
  } = useGetSearch(t5, t6);
  let t7;
  if ($[11] !== data?.creators) {
    t7 = data?.creators ?? [];
    $[11] = data?.creators;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== t7) {
    t8 = t7.flatMap(_temp);
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  const options = t8;
  let t9;
  if ($[15] !== emptyLabel || $[16] !== onChange || $[17] !== options || $[18] !== placeholder || $[19] !== query || $[20] !== value) {
    t9 = <SearchableMultiSelect value={value} onChange={onChange} placeholder={placeholder} emptyLabel={emptyLabel} options={options} controlledQuery={query} onQueryChange={setQuery} />;
    $[15] = emptyLabel;
    $[16] = onChange;
    $[17] = options;
    $[18] = placeholder;
    $[19] = query;
    $[20] = value;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  return t9;
}
function _temp(entry) {
  const label = creatorLabel(entry.creator);
  if (label === null) {
    return [];
  }
  return [{
    value: entry.creator.walletAddress,
    label
  }];
}